// eslint-disable-next-line max-classes-per-file
import CX from 'classnames'
import React from 'react'
import { Icon } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import Client from '../../client'
import CHANNELS from '../../constants/Channels'

interface Props {
  marketId: any
  path: any
  onChange: Function
  validateLeaf?: boolean
  breadcrumbs?: any
}

interface State {
  data: any
  path: any
  activeCategory: any
}

// @todo needs loading state
export default class CascadePicker extends React.Component<Props, State> {
  constructor(props) {
    super(props)
    this.state = {
      // loading: false,
      activeCategory: {},
      path: props.path || [],
      data: [{ categories: [] }]
    }
  }

  componentDidMount() {
    this.fetchInitial()
  }

  componentDidUpdate = prevProps => {
    if (prevProps.marketId !== this.props.marketId) this.fetchInitial()
  }

  // We only want to show non-deprecated CMCs for users to choose,
  // but still show the current value (even if deprecated), so the form makes sense.
  filterCmcs = (results, activeCategoryId) => {
    // Rolling this out for ebay will be more complicated
    if (this.props.marketId === CHANNELS.GOODWILLFINDS.id || this.props.marketId === CHANNELS.ETSY.id) {
      return results.map(o => {
        return {
          categories: o.categories.filter(cmc => {
            // Always show non-leafs. In at least one case (CMC 10848) we have a non-leaf CMC
            // that's marked as deprecated by one of its child categories. We need to show parent so we can show the child categories.
            // removed cmc.isLeaf check because it shows nonLeaf duplicate categories
            return cmc.deprecatedById === null || cmc.id === activeCategoryId
          })
        }
      })
    }

    return results
  }

  fetchInitial = async () => {
    const { marketId } = this.props
    const path = this.props.path || []
    let activeCategory: { id?: any } = {}

    const results = (
      await Promise.all([
        Client.categoryMarketCategories({ marketId, depth: 0 }),
        ...path.map(p => Client.categoryMarketCategories({ marketId, children: p }))
      ])
    ).map(r => ({ categories: r.data.data }))

    if (path.length) {
      activeCategory = results[results.length - 2].categories.find(c => c.id === path[path.length - 1])
    }

    this.setState({
      data: this.filterCmcs(results, activeCategory.id),
      activeCategory
    })
  }

  handleClick = async (depth, cat) => {
    const { path, data } = this.state

    const newPath = path.slice(0, depth).concat(cat.id)
    const currentCategories = data[depth].categories
    const newCategory = { ...currentCategories.find(c => c.id === cat.id) }

    this.setState({ path: newPath, activeCategory: cat }, this.props.onChange(newCategory, newPath))

    const res = await Client.categoryMarketCategories({ marketId: this.props.marketId, children: cat.id })
    this.setState(state => ({
      data: state.data
        .slice(0, depth + 1)
        .concat(this.filterCmcs([{ categories: res.data.data }], state.activeCategory.id))
    }))
  }

  render() {
    const isValid = this.props.validateLeaf ? this.state.activeCategory.isLeaf : true
    const cn = this.state.path.length > 0 ? { 'is-valid': isValid, 'is-invalid': !isValid } : ''

    return (
      <div>
        {this.props.breadcrumbs && (
          <ul className="bp3-breadcrumbs">
            <li onClick={() => this.fetchInitial()}>
              <a className="bp3-breadcrumb">All</a>
            </li>
            {this.state.path.map((p, depth) => (
              <li key={p} onClick={() => this.handleClick(depth, p)}>
                <a className={CX('bp3-breadcrumb', { 'bp3-breadcrumb-current': depth === this.state.path.length - 1 })}>
                  {this.state.data[depth].categories.find(c => c.id === p).name}
                </a>
              </li>
            ))}
          </ul>
        )}

        <div style={{ display: 'flex', marginBottom: 10 }} className={CX('cascade-picker', cn)}>
          {this.state.data.map((d, depth) => {
            if (!d.categories.length) return null
            return (
              <Panel
                depth={depth}
                handleClick={this.handleClick}
                categories={d.categories}
                key={depth /*eslint-disable-line */}
                path={this.state.path[depth]}
              />
            )
          })}
        </div>
      </div>
    )
  }
}

interface PanelProps {
  path: any
  categories: any
  handleClick: any
  depth: any
}

class Panel extends React.Component<PanelProps, {}> {
  private panelRef: any

  constructor(props) {
    super(props)
    this.panelRef = null
  }

  componentDidMount() {
    const { path, categories } = this.props
    const catIndex = categories.findIndex(c => c.id === path)
    if (catIndex <= 0) return
    const lis = this.panelRef.querySelectorAll('li')
    if (lis.length && typeof lis[catIndex].scrollIntoView === 'function') {
      lis[catIndex].scrollIntoView({ block: 'center', inline: 'nearest' })
    }
  }

  render() {
    const { handleClick, categories, path, depth } = this.props

    return (
      <ul
        className={CX('bp3-menu bp3-elevation-1')}
        style={{
          maxHeight: 300,
          maxWidth: '225px',
          overflow: 'scroll'
        }}
        ref={ref => {
          this.panelRef = ref
        }}
      >
        {categories.map(c => {
          const active = path === c.id

          return (
            <li key={c.pathCache} onClick={() => handleClick(depth, c)}>
              <a
                style={{ transition: active && '.2s' }}
                className={CX('bp3-menu-item', {
                  'bp3-active': active
                })}
                tabIndex={0}
              >
                {c.name}
                {!c.isLeaf && <Icon style={{ marginLeft: 'auto' }} icon={IconNames.CARET_RIGHT} />}
              </a>
            </li>
          )
        })}
      </ul>
    )
  }
}
